import { Menu } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { IconButton } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import {
	queryClient,
	useAuthUser,
	useDataQualityAccess,
	useSuspenseFeatureAccess,
} from '../../../api';
import { CUSTOM_PROPERTY_COLUMN_PREFIX } from '../../../constants';
import type { UseColumnDefsArgs } from '../hooks/useColumnDefs';

import { catalogQueryKeyFactory } from '../../../api/hooks/catalog/constants';
import { STICKY_COLUMNS } from '../../TableV2/constants';
import type { ExtendedDataTableColumn } from '../../TableV2/types';
import { useColumnDefs } from '../hooks/useColumnDefs';
import MenuList from './MenuList';

interface ICustomizeColumnsPanelProps<T>
	extends Pick<UseColumnDefsArgs<T>, 'catalogType' | 'catalogServerType'> {
	defaultColumns: ExtendedDataTableColumn<T>[];
	referenceId?: null | string;
	onChangeVisibility?: () => void;
	onChangeOrder?: () => void;
}

function CustomizeColumnsPanel<T>({
	defaultColumns,
	catalogType,
	catalogServerType,
	referenceId = null,
	onChangeVisibility,
	onChangeOrder,
}: ICustomizeColumnsPanelProps<T>) {
	const { catalog, onColumnReorder, onColumnVisibilityChange } = useColumnDefs({
		defaultColumns,
		catalogServerType,
		catalogType,
		entityId: referenceId,
	});

	const { user, workspace } = useAuthUser();
	const { collectionsAccess, secodaCoreAccess } = useSuspenseFeatureAccess();
	const dqsEnabled = useDataQualityAccess();

	const propertiesToDisplay = useMemo(
		() =>
			catalog?.properties
				.filter((p) => !STICKY_COLUMNS.includes(p.value))
				// No longer show (v1) (legacy) custom properties.
				.filter((p) => !p.value.startsWith(CUSTOM_PROPERTY_COLUMN_PREFIX))
				// Hide collections column if collectionsAccess is false
				.filter((p) => {
					if (p.value === 'collections') {
						return collectionsAccess;
					}
					if (p.value === 'pii') {
						return secodaCoreAccess;
					}
					if (p.value === 'dqs') {
						return dqsEnabled;
					}
					return true;
				})
				.filter((p) =>
					defaultColumns.find(
						(c) => c.accessor === p.value || c.esAccessor === p.value
					)
				)
				.map((p) => ({
					...p,
					label:
						defaultColumns.find(
							(c) => c.accessor === p.value || c.esAccessor === p.value
						)?.titleLabel || p.value,
				})) || [],
		[
			catalog?.properties,
			collectionsAccess,
			secodaCoreAccess,
			dqsEnabled,
			defaultColumns,
		]
	);

	const handleColumnOrderChange = useCallback(
		async (columnName: string, toIndex: number) => {
			// If the displayed columns are difference from the actual columns,
			// the target index is the index of the displayed column IN THE ORIGINAL ARRAY
			if (toIndex < 0 || toIndex >= propertiesToDisplay.length) {
				return;
			}
			const targetIndex = propertiesToDisplay[toIndex].order;
			const reorderedCatalog = await onColumnReorder(columnName, targetIndex);
			if (reorderedCatalog) {
				onChangeOrder?.();
				queryClient.invalidateQueries({
					queryKey: catalogQueryKeyFactory.all(),
				});
			}

			trackEvent('catalog/customize/reorder', {}, user, workspace);
		},
		[onChangeOrder, onColumnReorder, user, workspace, propertiesToDisplay]
	);

	const handleVisibilityChange = useCallback(
		async (columnName: string, isVisible: boolean) => {
			await onColumnVisibilityChange(columnName, isVisible);
			onChangeVisibility?.();
			queryClient.invalidateQueries({
				queryKey: catalogQueryKeyFactory.all(),
			});

			trackEvent(
				'catalog/customize/toggle_column',
				{
					column: columnName,
				},
				user,
				workspace
			);
		},
		[onChangeVisibility, onColumnVisibilityChange, user, workspace]
	);

	return (
		<Menu width={250} position="bottom-end" withinPortal>
			<Menu.Target>
				<IconButton
					data-testid="customize-columns-button"
					iconName="adjustmentsHorizontal"
					variant="tertiary"
					tooltip="Rearrange columns"
				/>
			</Menu.Target>
			<Menu.Dropdown>
				<MenuList
					rowHeight={32}
					disableDragging={false}
					catalogProperties={propertiesToDisplay}
					onColumnOrderChange={handleColumnOrderChange}
					onVisibilityChange={handleVisibilityChange}
				/>
			</Menu.Dropdown>
		</Menu>
	);
}

export default CustomizeColumnsPanel;
